import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "../axiosInstance";
import { toast } from "react-toastify";
import { Box, CircularProgress, Typography } from "@mui/material";
import LOGO_BASE64 from "../assets/data/logoBase64";
import dayjs from "dayjs";
import { calculateTotals, calculatePastTransactionsBalance } from "../utils/calculateTotals";

const TransactionViewer = () => {
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const initialized = useRef(false);

    const customerId = searchParams.get("customerId");
    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");

    useEffect(() => {
        // Prevent double execution in Strict Mode
        if (!customerId || initialized.current) return;
        
        initialized.current = true;
        fetchAndPrint();
    }, [customerId]);

    const fetchAndPrint = async () => {
        try {
            setLoading(true);

            const payload = { customerId };
            if (startDate && endDate) {
                payload.startDate = dayjs(startDate).format("YYYY-MM-DD");
                payload.endDate = dayjs(endDate).format("YYYY-MM-DD");
            }

            const [custRes, txRes, allTxRes] = await Promise.all([
                axiosInstance.post("/customers/details", { customerId }),
                axiosInstance.post("/transactions/list", payload),
                (startDate && endDate)
                    ? axiosInstance.post("/transactions/list", { customerId })
                    : Promise.resolve(null),
            ]);

            const customerDetails = custRes?.data?.result || {};
            const transactionList = txRes?.data?.result || [];
            const trayTypes = Array.isArray(customerDetails?.trayBalance) ? customerDetails.trayBalance : [];
            const allTransactions = allTxRes?.data?.result || transactionList;

            if (transactionList.length === 0) {
                toast.info("No transactions found for the selected period.");
                setLoading(false);
                return;
            }

            const sortedTransactionList = [...transactionList].sort(
                (a, b) => {
                    const dateDifference =
                        new Date(a.dateOfTransaction).getTime() -
                        new Date(b.dateOfTransaction).getTime();
                    if (dateDifference) return dateDifference;

                    const timeDifference =
                        new Date(a.createdAt).getTime() -
                        new Date(b.createdAt).getTime();
                    if (Number.isFinite(timeDifference) && timeDifference) {
                        return timeDifference;
                    }

                    return String(a._id || "").localeCompare(String(b._id || ""));
                }
            );
            const totals = calculateTotals(transactionList);
            const { 
              grandTotalTransactionAmount,
              discountAmount,
              labourCharges,
              grandTotalPaidAmount,
              damageRefund,
              grandTrayPaymentAmount,
              totalPaid,
              totalDueAmount
            } = totals;

            generatePrint(
                sortedTransactionList,
                customerDetails,
                trayTypes,
                {
                    grandTotalTransactionAmount,
                    discountAmount,
                    labourCharges,
                    grandTotalPaidAmount,
                    grandTrayPaymentAmount,
                    damageRefund,
                    totalPaid,
                    totalDueAmount
                },
                startDate,
                endDate,
                allTransactions
            );

        } catch (err) {
            console.error(err);
            toast.error("Failed to load transaction data");
        } finally {
            setLoading(false);
        }
    };

    const generatePrint = (list, customer, trayTypes, totals, sDate, eDate, allTransactions = []) => {
        const val = (v) => v ?? "-";
        const formatIndianAmount = (amount) => {
            const numericAmount = Number(amount);
            return amount == null || !Number.isFinite(numericAmount)
                ? "-"
                : numericAmount.toLocaleString("en-IN");
        };

        // Create the print document
        window.document.write(`
      <html>
      <head>
        <title>Transactions - ${customer?.name}</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 30px; color: #333; }
          .header { display: flex; justify-content: space-between; border-bottom: 3px solid #1976d2; padding-bottom: 10px; margin-bottom: 20px; }
          .company-name { font-size: 24px; font-weight: bold; color: #1976d2; }
          .summary-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin-bottom: 20px; background: #f5f5f5; padding: 15px; border-radius: 8px; }
          .summary-item { text-align: center; }
          .summary-label { font-size: 10px; color: #666; text-transform: uppercase; }
          .summary-value { font-size: 14px; font-weight: bold; }
          
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th { background: #f0f0f0; font-weight: bold; border: 1px solid #ccc; padding: 6px; }
          td { border: 1px solid #ccc; padding: 6px; text-align: center; }
          
          .tray-column { background-color: #e4ffe4 !important; -webkit-print-color-adjust: exact; }
          .damage-text { background-color: #ffebee; color: #c62828; font-weight: bold; }
          .labour-text { color: #1976d2; font-style: italic; }
          .balance-column { background-color: #f0f7ff !important; font-weight: bold; color: #1976d2; -webkit-print-color-adjust: exact; }
          .proprietor-meta { font-size: 13px; color: #555; margin-top: 4px; }
        </style>
      </head>
      <body>
        <div class="header">
            <div style="display: flex; align-items: center; gap: 12px;">
                <img src="${LOGO_BASE64}" style="width: 50px; height: 50px; border-radius: 4px;" />
                <div>
                    <div style="font-size: 20px; font-weight: bold; color: #1976d2;">${customer?.employerDetails?.employerName || "-"}</div>
                    <div style="color: #666; font-size: 16px;">${customer?.branchName || "-"}</div>
                    <div class="proprietor-meta">
                        Proprietor: <strong>Maddireddy Ramesh</strong> &bull; Contact: <strong>+91 76666 77779</strong>
                    </div>
                </div>
            </div>
        </div>

        <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 20px; padding: 4px 0;">
          <div>
            <div style="font-size: 18px; font-weight: bold; text-transform: capitalize; color: #222;">${customer?.name || "-"}</div>
            <div style="color: #666; font-size: 13px; margin-top: 4px;">${customer?.mobile || ""}${customer?.mobile && customer?.location ? " • " : ""}${customer?.location || ""}</div>
          </div>
          <div style="text-align: right;">
            <div style="font-size: 12px; color: #666; text-transform: uppercase; font-weight: 700; letter-spacing: 0.5px;">Total Balance Due</div>
            <div style="font-size: 28px; font-weight: 800; color: #1976d2; margin-top: 2px; line-height: 1.2;">
                ₹${formatIndianAmount(Math.round(totals.totalDueAmount || 0))}
            </div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th rowspan="2">Date</th>
                            <th colspan="4">Sales</th>
              <th colspan="3" class="tray-column">Trays Returned</th>
              <th colspan="2">Payment</th>
              <th rowspan="2" class="balance-column">Balance</th>
            </tr>
            <tr>
                            <th>Item</th><th>Qty</th><th>Price</th><th>Total</th>
              <th class="tray-column">Qty</th><th class="tray-column">Price</th><th class="tray-column">Total</th>
              <th>Cash</th><th>Tray</th>
            </tr>
          </thead>
          <tbody>
            ${list.map(tx => {
                let salesRows = tx.sales ? [...tx.sales] : [];

                if (tx.labourCharges?.enabled) {
                    salesRows.push({
                        itemName: "Loading Charges",
                        numberOfItems: tx.labourCharges.numberOfItems,
                        costPerItem: tx.labourCharges.costPerItem,
                        total: tx.labourCharges.total,
                        isLabour: true,
                    });
                }

                if (Number(tx.discount) > 0) {
                    salesRows.push({
                        itemName: "Discount",
                        numberOfItems: "-",
                        costPerItem: "-",
                        total: -Number(tx.discount),
                        isDiscount: true,
                    });
                }

                if (tx?.damageRefunds?.length > 0) {
                    const tempDamage = tx.damageRefunds.map(d => ({ 
                        ...d, 
                        isDamage: true,
                        itemName: `${d.itemName || 'Item'} (Refund)`
                    }));
                    salesRows = [...salesRows, ...tempDamage];
                }

                const trays = tx.trays || [];
                const rowCount = Math.max(salesRows.length, trays.length, 1);
                const pAmt = Number(tx.paymentAmount || 0);
                const tTotal = trays.reduce((s, t) => s + (Number(t.numberOfTrays || 0) * Number(t.costPerTray || 0)), 0);
                const txBalance = calculatePastTransactionsBalance(
                    tx,
                    allTransactions && allTransactions.length > 0 ? allTransactions : list,
                    trayTypes
                );
                const formattedBalance = txBalance < 0
                    ? `-₹${formatIndianAmount(Math.round(Math.abs(txBalance)))}`
                    : `₹${formatIndianAmount(Math.round(txBalance))}`;

                return Array.from({ length: rowCount }).map((_, i) => {
                    const sale = salesRows[i];
                    const tray = trays[i];
                    
                    return `
                        <tr>
                            <!-- Column 1: Date -->
                            ${i === 0 ? `<td rowspan="${rowCount}">${dayjs(tx.dateOfTransaction).format("DD-MMM-YYYY")}</td>` : ""}
                            
                            <!-- Columns 2-5: Sales Details -->
                            <td class="${sale?.isDamage ? 'damage-text' : ''} ${sale?.isLabour ? 'labour-text' : ''}">
                                ${val(sale?.itemName)}
                            </td>
                            <td class="${sale?.isDamage ? 'damage-text' : ''}">${val(sale?.numberOfItems)}</td>
                            <td class="${sale?.isDamage ? 'damage-text' : ''}">${formatIndianAmount(sale?.costPerItem)}</td>
                            <td class="${sale?.isDamage ? 'damage-text' : ''}">${formatIndianAmount(sale?.total)}</td>
                            
                            <!-- Columns 6-8: Trays Details -->
                            <td class="tray-column">${val(tray?.numberOfTrays)}</td>
                            <td class="tray-column">${formatIndianAmount(tray?.costPerTray)}</td>
                            <td class="tray-column">${tray ? formatIndianAmount(Number(tray.numberOfTrays || 0) * Number(tray.costPerTray || 0)) : "-"}</td>

                            <!-- Columns 9-11: Payment Stack Summary & Balance -->
                            ${i === 0 ? `
                                <td rowspan="${rowCount}">${formatIndianAmount(pAmt)}</td>
                                <td rowspan="${rowCount}">${formatIndianAmount(tTotal)}</td>
                                <td rowspan="${rowCount}" class="balance-column">${formattedBalance}</td>
                            ` : ""}
                        </tr>
                    `;
                }).join("");
            }).join("")}
          </tbody>
        </table>
      </body>
      </html>
    `);

        window.document.close();
        window.focus();
    };

    return (
        <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
            {loading ? (
                <Box textAlign="center">
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Preparing your document...</Typography>
                </Box>
            ) : (
                <Typography>Print session finished. You can close this tab.</Typography>
            )}
        </Box>
    );
};

export default TransactionViewer;